import * as React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { RichTextEditor } from '../components/RichTextEditor';
import CloseIcon from '@mui/icons-material/Close';

type Category = {
  categoryId: number;
  name: string;
  description: string;
  hasSubcategories: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

const API_URL = import.meta.env.VITE_API_URL;

const CategoryList = () => {
  const [data, setData] = React.useState<Category[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [index, setIndex] = React.useState<number>(1);
  const [hasSubcategories, setHasSubcategories] = React.useState(false);
  const [error, setError] = React.useState('');
  const navigate = useNavigate();

  React.useEffect(() => {
    axios
      .get(`${API_URL}/categories`)
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id: number) => {
    await axios.delete(`${API_URL}/categories/${id}`);
    setData((prev) => prev.filter((item) => item.categoryId !== id));
  };

  const handleCreate = async () => {
    setError('');
    try {
      const res = await axios.post(`${API_URL}/categories`, {
        name,
        description,
        hasSubcategories,
        index,
      });

      setData(res.data);
      setOpen(false);
      setName('');
      setDescription('');
      setIndex(1);
      setHasSubcategories(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Сталася помилка');
    }
  };

  if (loading) return null;

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        mt={3}
      >
        <Typography variant="h5">Категорії</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>
          Додати категорію
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Назва</TableCell>
              <TableCell>Опис</TableCell>
              <TableCell align="center">Підкатегорії</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((row) => (
              <TableRow
                key={row.categoryId}
                sx={{ opacity: row.isActive ? 1 : 0.4 }}
              >
                <TableCell>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                    dangerouslySetInnerHTML={{ __html: row.name }}
                  />
                </TableCell>
                <TableCell>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                    dangerouslySetInnerHTML={{ __html: row.description }}
                  />
                </TableCell>
                <TableCell align="center">
                  {row.hasSubcategories ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <CancelIcon color="error" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() =>
                      navigate(`/categories/${row.categoryId}/edit`)
                    }
                  >
                    Редагувати
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    sx={{ ml: 1 }}
                    onClick={() => handleDelete(row.categoryId)}
                  >
                    Видалити
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Додати категорію
          <IconButton edge="end" color="inherit" onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Назва категорії"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            style={{ marginBottom: 20 }}
          />

          <RichTextEditor
            value={description}
            onChange={(html) => setDescription(html)}
            label="Опис категорії"
            minRows={4}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={hasSubcategories}
                onChange={(e) => setHasSubcategories(e.target.checked)}
              />
            }
            label="Є підкатегорії"
          />
          <TextField
            type="number"
            label="Порядок"
            fullWidth
            margin="normal"
            value={index}
            inputProps={{ min: 1, max: 100 }}
            onChange={(e) =>
              setIndex(Math.min(100, Math.max(1, Number(e.target.value))))
            }
          />
          {error && (
            <Typography color="error" mt={1}>
              {error}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Відміна</Button>
          <Button variant="contained" onClick={handleCreate}>
            Додати
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryList;
